import { AdjustmentsVerticalIcon, ArrowTopRightOnSquareIcon, DocumentDuplicateIcon, GlobeAltIcon, PencilSquareIcon, UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import Button from "src/components/Shared/Buttons/Button";
import SelectMenu from "src/components/Shared/Forms/Selects/SelectMenu";
import UserProfile from "src/components/Users/UserProfile";
import { classNames } from "src/helpers/classNames";
import { getClientTypeBadgeStyles } from "src/helpers/clientHelper";

export const ClientOverview = ({ data, defaultUser, clientTypes, domain, handleTypeChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedType, setSelectedType] = useState(data?.client_type);
  const { className, label } = getClientTypeBadgeStyles(selectedType);

  useEffect(() => {
    setSelectedType(data?.client_type);
  }, [data?.client_type]);

  return (
    <div className="animate-fade-in  bg-white shadow-sm">
      <div className="space-y-5 p-2">
        <div className="grid h-16 grid-cols-12 items-center gap-6">
          <div className="col-span-5 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-50">
              <UserIcon className="h-6 w-6 text-highlightColor" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Client name</h3>
              <p className="text-sm text-gray-600">The administrator of this portal</p>
            </div>
          </div>

          <div className="col-span-7 flex items-center justify-start gap-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 overflow-hidden rounded-full">
                <UserProfile
                  size="lg"
                  user={defaultUser?._id ? defaultUser : { name: data?.name, email: data?.email }}
                  className="!bg-highlightColor !text-white"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{`${defaultUser?.first_name || ""} ${defaultUser?.last_name || ""}`.trim()}</h1>
                <p className="text-sm text-gray-500">{defaultUser?.email}</p>
              </div>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className="grid h-16 grid-cols-12 items-center gap-6">
          <div className="col-span-5 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-50">
              <GlobeAltIcon className="h-6 w-6 text-highlightColor" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Domain Configuration</h3>
              <p className="text-sm text-gray-600">Client portal access URL</p>
            </div>
          </div>

          <div className="col-span-7">
            <div className="relative flex w-full items-center gap-5 rounded border">
              <input
                type={"domain"}
                value={domain ? domain + "/manage" : ""}
                className="h-12 w-full rounded border-0 bg-gray-200/40 px-3 py-0 pr-10 font-semibold !ring-0"
                onChange={() => {}}
              />
              <div className="absolute right-1 top-1 z-10 flex gap-x-1">
                <CopyToClipboard
                  text={domain ? domain + "/manage" : ""}
                  onCopy={() => toast.success("Copied")}>
                  <Button
                    type="button"
                    version="default"
                    className="flex !h-10 !w-10 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 shadow-sm">
                    <DocumentDuplicateIcon className="h-5 w-5 stroke-2" />
                  </Button>
                </CopyToClipboard>
                <div className="flex !h-10 !w-10 items-center justify-center rounded border border-gray-200 bg-white text-gray-500 shadow-sm">
                  <a
                    href={domain ? `https://${domain}/manage` : ""}
                    rel="noreferrer"
                    target="_blank"
                    type="button"
                    version="default"
                    className="flex !h-8 !w-8 items-center justify-center rounded bg-white text-gray-500 shadow-sm">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5 stroke-2" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className="grid h-16 grid-cols-12 items-center gap-6">
          <div className="col-span-5 flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-purple-50">
              <AdjustmentsVerticalIcon className="h-6 w-6 text-highlightColor" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Client Type</h3>
              <p className="text-sm text-gray-600">Select or update the client category</p>
            </div>
          </div>

          <div className="col-span-7 flex items-center gap-4">
            {!isEditing ? (
              <div className="flex w-full items-center gap-2">
                <div>{data?.client_type && <div className={classNames("flex h-10 items-center justify-center whitespace-nowrap rounded-lg  px-6 text-sm font-semibold  transition-all duration-200 group-hover:bg-white", className)}>{label}</div>}</div>

                <button
                  onClick={() => setIsEditing(true)}
                  className="rounded-md border border-gray-200 bg-white p-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100">
                  <PencilSquareIcon className="h-5 w-5" />
                </button>
              </div>
            ) : (
              <div className="flex w-full items-center justify-start gap-3">
                <div className="flex w-full gap-x-3">
                  <SelectMenu
                    inline
                    classes="!h-14 min-w-[200px]"
                    label="Client type"
                    startIndex={clientTypes?.findIndex((c_type) => c_type?.value === selectedType)}
                    options={clientTypes || []}
                    setOption={(option) => setSelectedType(option.value)}
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        if (selectedType === "subscription") {
                          handleTypeChange({ client_type: selectedType, triggerPlanAssign: true });
                        } else {
                          handleTypeChange({ client_type: selectedType });
                        }
                        setIsEditing(false);
                      }}
                      className="rounded-md bg-highlightColor p-2 text-sm font-medium text-white transition">
                      <CheckIcon className="h-5 w-5" />
                    </button>

                    <button
                      onClick={() => setIsEditing(false)}
                      className="rounded-md border border-gray-300 bg-white p-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100">
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
